// src/components/communs/LecteurEvaluationRich.jsx
// Équivalent de LecteurEvaluation.jsx pour les évaluations enrichies (EvaluationRich).
//
// ROUTES ÉTUDIANT (vérifiées dans EtudiantController.java) :
//   GET  /api/v1/etudiant/rich/evaluations/{id}?etudiantId=...
//   POST /api/v1/etudiant/rich/evaluations/{id}/demarrer?etudiantId=...   (appelé par EvaluationRich.jsx)
//   POST /api/v1/etudiant/rich/evaluations/{id}/soumettre?etudiantId=...
//   GET  /api/v1/etudiant/rich/evaluations/{id}/resultat?etudiantId=...
//
// Correction TOUJOURS manuelle (SOMMATIVE / EXAMEN / CONCOURS) : aucun score
// n'est affiché à l'étudiant après soumission.
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';
import EvaluationRich from './EvaluationRich';
import { Loader2, AlertCircle } from 'lucide-react';

export default function LecteurEvaluationRich({
    evaluationId,
    etudiantId,
    onClose,
    onSubmitSuccess
}) {
    const { token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [evaluation, setEvaluation] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [resultat, setResultat] = useState(null);

    useEffect(() => {
        const fetchEvaluation = async () => {
            try {
                // ⭐ CORRECTION : le contrôleur expose
                // /api/v1/etudiant/rich/evaluations/{id} (et non
                // /evaluations-rich/{id}) — l'ancien chemin renvoyait 404.
                const response = await fetch(
                    `${APP_CONFIG.BASE_URL}/api/v1/etudiant/rich/evaluations/${evaluationId}?etudiantId=${etudiantId}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (!response.ok) throw new Error("Erreur chargement de l'évaluation enrichie");
                const data = await response.json();
                setEvaluation(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        if (evaluationId && token) fetchEvaluation();
    }, [evaluationId, token, etudiantId]);

    // reponses : [{ questionIndex, reponse }] — construit par EvaluationRich.jsx.
    // ⚠️ Le champ s'appelle bien "reponse" : côté backend, EtudiantRichServiceImpl
    // lit dto.getReponse() sur ReponseQCMRichDTO. Un payload envoyant
    // "reponseCandidat" serait désérialisé avec reponse = null et toutes les
    // réponses seraient perdues silencieusement (pas d'erreur HTTP).
    const handleSubmit = async (reponses) => {
        setSubmitting(true);
        try {
            // ⭐ CORRECTION : le contrôleur définit l'action de soumission sous
            // /api/v1/etudiant/rich/evaluations/{id}/soumettre (verbe
            // "soumettre", pas "repondre", et préfixe "rich/evaluations" pas
            // "evaluations-rich"). L'ancien chemin renvoyait 404.
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/etudiant/rich/evaluations/${evaluationId}/soumettre?etudiantId=${etudiantId}`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(reponses)
                }
            );
            if (!response.ok) {
                // Le backend renvoie des messages métier utiles (délai dépassé,
                // évaluation déjà soumise, session non démarrée) : on les remonte
                // à l'étudiant au lieu d'un message générique.
                let msg = 'Erreur lors de la soumission des réponses';
                try {
                    const body = await response.json();
                    if (body?.message) msg = body.message;
                } catch (err) { 
					console.error('Erreur ', err);
					/* corps non JSON : on garde le message par défaut */ }
                throw new Error(msg);
            }
            // Le serveur renvoie un ResultatRich (statut, totalQuestions, ...).
            // ⚠️ EvaluationRich = SOMMATIVE / EXAMEN / CONCOURS : la correction est
            // manuelle, donc on ne se sert JAMAIS de ce retour pour afficher un
            // score à l'étudiant. Seul le statut (ex. EN_ATTENTE_CORRECTION) est
            // exploité par EvaluationRich.jsx.
            const serverResult = await response.json();
            console.log('Résultat évaluation enrichie (serveur) :', serverResult);
            setResultat(serverResult);

            if (onSubmitSuccess) {
                onSubmitSuccess(serverResult);
            }
        } catch (err) {
            console.error('Erreur soumission évaluation enrichie:', err);
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center max-w-md px-4">
                    <AlertCircle className="w-12 h-12 sm:w-14 sm:h-14 text-red-500 mx-auto mb-4" />
                    <p className="text-sm sm:text-base text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    if (!evaluation) return null;

    return (
        <EvaluationRich
            evaluation={evaluation}
            formationTitre={evaluation.titre || 'Évaluation'}
            moduleOrdre={1}
            totalModules={1}
            onClose={onClose}
            isOpen={true}
            onSubmit={handleSubmit}
            onSubmitSuccess={onSubmitSuccess}
            submitting={submitting}
            resultat={resultat}
            evaluationId={evaluationId}
            etudiantId={etudiantId}
            // ⭐ CORRECTION : requis pour l'appel interne /demarrer d'EvaluationRich
            token={token}
        />
    );
}